/**
 * Dev route: straight into a mission, no menus. `/?quick=1` flies the hand-built depot with
 * a sensible loadout; `&drop=0` starts on the ground; `&archetype=... &biome=... &tod=...
 * &weather=... &threat=... &seed=...` flies a generated mission instead. `&modifiers=` and
 * `&intel=` take comma-separated ids, so a card or a condition can be looked at without a campaign.
 */

import type { WebGLRenderer } from 'three';
import { ARCHETYPE_IDS, generateMission } from '../gen';
import { buildMunitionsDepot } from '../sim/missions/munitionsDepot';
import { STANDARD_ISSUE } from '../sim/tuning';
import type { ArchetypeId, Loadout, MissionDef, MissionParams } from '../sim/types';
import { startMission } from './missionApp';
import { num, pick, query } from './params';

/** The C64 original's fixed pod: 2 magazines, 2 grenades, 1 LAW, 1 bomb, 1 first-aid kit (01 §4). */
const CLASSIC_POD = { magazines: 2, roundsInMag: 0, grenades: 2, laws: 1, bombs: 1, firstAid: 1 };

/** A comma-separated query parameter as a list of ids, empty when it is absent or blank. */
const list = (name: string): string[] =>
  (query.get(name) ?? '').split(',').map((v) => v.trim()).filter((v) => v !== '');

function missionFromUrl(): MissionDef {
  const seed = num('seed', 1987);
  const archetype = query.get('archetype') as ArchetypeId | null;
  if (!archetype || !ARCHETYPE_IDS.includes(archetype)) return buildMunitionsDepot(seed);
  return generateMission({
    archetype,
    biome: pick('biome', ['temperate', 'arctic', 'desert'] as const, 'temperate'),
    timeOfDay: pick('tod', ['day', 'dusk', 'night', 'dawn'] as const, 'day'),
    weather: pick('weather', ['clear', 'snow', 'blizzard', 'rain', 'sandstorm', 'thunderstorm'] as const, 'clear'),
    threat: num('threat', 3),
    seed,
    // `&modifiers=storm,night-op`. Without this there was no way to reach a modifier by hand at
    // all - so the Storm card, and the lightning that comes with it, could not be looked at.
    modifiers: list('modifiers') as MissionParams['modifiers'],
    // `&intel=patrol-schedule,recon-photos`. ROUND5 gave the cards real effects on the map and in the
    // sim; without this there is no way to look at one by hand, which is how the dead ones went unseen.
    intel: list('intel') as MissionParams['intel'],
  });
}

export function startQuickMission(canvas: HTMLCanvasElement, renderer: WebGLRenderer): void {
  const loadout: Loadout = { carried: { ...STANDARD_ISSUE }, pods: [CLASSIC_POD, CLASSIC_POD, CLASSIC_POD].map((p) => ({ ...p })) };
  startMission(canvas, renderer, {
    def: missionFromUrl(),
    loadout,
    skipInsertion: query.get('drop') === '0',
    pitch: num('pitch', 45),
    ppm: num('ppm', 18),
    showHud: query.get('hud') !== '0',
  });
}
